import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { api } from '../App';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';

export default function AssessmentQuestions({ user }) {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ageGroup, setAgeGroup] = useState('child');

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      // First get assessment to know target_user_id
      const assessmentResponse = await api.get('/assessments');
      const currentAssessment = assessmentResponse.data.find(a => a.id === assessmentId);
      
      const params = { assessment_type: 'student_self' };
      if (currentAssessment?.target_user_id) {
        params.target_user_id = currentAssessment.target_user_id;
      }
      
      const response = await api.get('/questions', { params });
      setQuestions(response.data.questions);
      setAgeGroup(response.data.age_group || 'child');
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Sorular yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    if (!answers[questions[currentIndex].id]) {
      toast.error('Lütfen bir seçenek seçin');
      return;
    }
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (!answers[questions[currentIndex].id]) {
      toast.error('Lütfen bir seçenek seçin');
      return;
    }

    setSubmitting(true);

    try {
      const answerArray = Object.entries(answers).map(([questionId, value]) => ({
        question_id: parseInt(questionId),
        answer_value: value
      }));

      const response = await api.post('/assessments/submit', {
        assessment_id: assessmentId,
        answers: answerArray
      });

      toast.success('Envanter tamamlandı! Raporunuz hazırlanıyor...');
      
      // Navigate to report
      setTimeout(() => {
        navigate(`/report/${response.data.report_id}`);
      }, 1500);
    } catch (error) {
      console.error('Failed to submit assessment:', error);
      toast.error('Gönderim sırasında hata oluştu');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const isLastQuestion = currentIndex === questions.length - 1;

  const likertOptions = [
    { value: 1, label: 'Hiç Bana Uymuyor', color: 'from-red-500 to-red-600' },
    { value: 2, label: 'Bana Pek Uymuyor', color: 'from-orange-500 to-orange-600' },
    { value: 3, label: 'Kararsızım', color: 'from-yellow-500 to-yellow-600' },
    { value: 4, label: 'Bana Uyuyor', color: 'from-lime-500 to-lime-600' },
    { value: 5, label: 'Tam Beni Anlatıyor', color: 'from-emerald-500 to-emerald-600' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 flex items-center justify-center p-6">
      <div className="w-full max-w-3xl animate-fade-in">
        <Card className="shadow-xl border-none" data-testid="assessment-question-card">
          <CardHeader>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <CardDescription>Soru {currentIndex + 1} / {questions.length}</CardDescription>
                <CardDescription>{Math.round(progress)}% Tamamlandı</CardDescription>
              </div>
              <Progress value={progress} className="h-2" data-testid="progress-bar" />
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Question */}
            <div className="text-center py-8">
              <h2 className="text-2xl font-semibold text-slate-800 leading-relaxed" data-testid="question-text">
                {currentQuestion.text}
              </h2>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {likertOptions.map((option) => {
                const isSelected = answers[currentQuestion.id] === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(currentQuestion.id, option.value)}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      isSelected
                        ? `bg-gradient-to-r ${option.color} text-white shadow-lg scale-105`
                        : 'bg-white hover:bg-slate-50 border-2 border-slate-200 hover:border-blue-300'
                    }`}
                    data-testid={`option-${option.value}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${isSelected ? 'text-white' : 'text-slate-800'}`}>
                        {option.label}
                      </span>
                      {isSelected && <Check className="w-5 h-5" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Navigation */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="flex-1"
                data-testid="previous-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Önceki
              </Button>
              {isLastQuestion ? (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700"
                  data-testid="submit-btn"
                >
                  {submitting ? 'Gönderiliyor...' : 'Tamamla'}
                  {!submitting && <Check className="w-4 h-4 ml-2" />}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700"
                  data-testid="next-btn"
                >
                  Sonraki
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}